package com.dynamicruntime.webapp

import com.dynamicruntime.common.gedra.traitNameOf
import com.dynamicruntime.common.gedra.workflow.TraitLockCopy
import com.dynamicruntime.common.gedra.workflow.WfPhase
import com.dynamicruntime.common.gedra.GDF
import com.dynamicruntime.common.schema.LAYSTR
import com.dynamicruntime.common.schema.SLDM
import com.dynamicruntime.common.schema.SchFailure
import com.dynamicruntime.common.util.analyzeTemplate
import kotlinx.browser.document
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.awaitCancellation
import kotlinx.coroutines.await
import kotlin.js.Promise
import kotlinx.coroutines.launch
import react.ChildrenBuilder
import react.FC
import react.Props
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.h1
import react.dom.html.ReactHTML.h2
import react.dom.html.ReactHTML.p
import react.dom.html.ReactHTML.span
import react.Key
import react.dom.html.ReactHTML.ul
import react.dom.html.ReactHTML.li
import react.useEffect
import react.useState
import web.cssom.ClassName
import com.dynamicruntime.common.util.escapeMarkdown
import com.dynamicruntime.common.util.humanizeFieldName

private val wfFormScope = MainScope()

external interface WorkflowFormProps : Props {
    /** The resolved workflow view to render -- a creation workflow, or a survey resolved against a form. */
    var view: WorkflowView

    /** The form a survey edit updates (issue #659); null for a creation workflow, which makes a new form. */
    var gedraId: String?

    /**
     * The client whose copy of the workflow save to post to (issue #714): the form's own, when it is another
     * client's than the caller's and that client has a copy; null posts to the shared path, bound to the
     * caller's own client. The page derives it from the view it fetched (`clientOfResolvedPath`), so the save
     * goes where the view came from.
     */
    var client: String?

    /**
     * Whether a survey edit opens already in edit mode (issue #694): the forms-list status chip's direct-to-edit
     * link sets it; the "View Info" action leaves it unset for the read-only view. Ignored for a creation form,
     * which is always editable.
     */
    var initialEditing: Boolean?

    /**
     * Opens the **raw** editor for this form (issue #694), offered as a "Raw edit" link on the survey's read-only
     * view. The survey is an on-boarding *subset* of traits; the raw editor edits every trait, including ones set
     * by API or other workflows. Null when the caller's surface has no patch endpoint (a control that could only
     * fail is not shown), or for a creation form.
     */
    var onRawEdit: (() -> Unit)?

    /**
     * Opens the raw **read-only** view of this form (issue #726), offered as "View raw" beside "Raw edit" on the
     * survey's read-only view: every trait, looked at rather than edited. Null for a creation form, which has no
     * stored form to view yet.
     */
    var onRawView: (() -> Unit)?

    /**
     * The task the rail shows (issue #700), for a multi-task survey; ignored (every task renders) for a
     * single-task survey or a creation form. The page chooses it from the URL or the view's earliest task
     * needing action.
     */
    var activeTask: String?

    /** Called when the user picks a task in the rail (issue #700); the page owns the choice, since it rides the
     *  address. Unset (with a single task) means no rail. */
    var onSelectTask: ((String) -> Unit)?

    /**
     * Told whether any task holds edits not yet saved (issue #700), each time that changes -- what the page arms
     * the leave guard on. Client-side: a working value differing from the last stored one.
     */
    var onDirtyChange: ((Boolean) -> Unit)?

    /**
     * Whether a **create** workflow may be run for another user (issue #727): true for an administrator, so the
     * creation form offers the [FormUserPicker] and the create save carries the chosen user. Ignored for a
     * survey edit (that acts on an existing form). Absent/false hides the picker -- the ordinary self-create.
     */
    var allowCreateForUser: Boolean?

    /**
     * Puts the form into this **normal workflow** (issue #791), offered as Engage in place of Edit when the view
     * says the form may engage ([WorkflowView.canEngage]). The page owns the call and the reload after it.
     */
    var onEngage: (() -> Unit)?

    /**
     * Told that an approval of task `taskId` has settled (issue #832) -- approved (`refusal` null), or refused by the
     * endpoint (a 4xx: already approved, not the current step, not a reviewer). Either way the form's state may have
     * moved, so the page reads the view again, staying on that step, and keeps any [approveRefusal] across the reload
     * so it is still shown by the step it concerns. A failure that never reached a verdict (the network, a 5xx) is not
     * reported here: the form shows it itself, since a reload would only fail too. Unset for a view with no approval.
     */
    var onApproveSettled: ((taskId: String, refusal: DisplayError?) -> Unit)?

    /** The refusal the last approval came back with (issue #832), kept by the page across the reload that followed. */
    var approveRefusal: ApproveRefusal?

    /**
     * Re-reads the view after task `taskId`'s edit save (issue #827), staying on that task. A save says only that the
     * form's state may have moved -- saving one task can move another's mark, a lock, the task needing action -- so the
     * page reads the whole view again **in place**: the form is not remounted, so the other tasks' unsaved edits
     * survive. Resolves with the fresh view once the page holds it, null when the form no longer resolves; rejects
     * when the read fails. Unset for a creation form, whose save leaves the page.
     */
    var refreshView: ((taskId: String) -> Promise<WorkflowView?>)?

    /**
     * Re-reads the view in place after a Design View edit of the workflow's copy has saved (issue #984), so the change
     * shows without a reload or a remount. Unset where the page has no such read; the edit then shows on the next.
     */
    var onDesignEdited: (() -> Unit)?
}

/** An approval's refusal (issue #832) and the task it concerns -- shown by that task's button, and nowhere else. */
class ApproveRefusal(val taskId: String, val error: DisplayError)

/**
 * Renders a resolved workflow and saves it (issues #536, #659). Each trait a task collects is drawn from *its
 * own resolved schema* (carried in the view's `$defs`) through the shared [SchemaForm], in the workflow's order
 * with its `required` flags. It serves two shapes on one component, matching the backend's one view/save
 * endpoint:
 *
 *  - **Creation** (`gedraId == null`): one task, always editable, its `create` save makes the form; on success
 *    the page returns to the listing it was launched from with the new row flashed (issue #758), through the
 *    one shared return every create surface uses (`formsCreateReturn`).
 *  - **Survey edit** (`gedraId != null`): the form's one-to-three tasks, each seeded from its current entries,
 *    shown **read-only with an Edit toggle** ("View Info"); editing reveals each task's `edit` save, which
 *    updates the form. Saving does not gate on completeness -- an incomplete survey is recorded as state, not
 *    refused -- so a required trait left empty comes back only through the status column, not as a block here.
 *
 * A field the schema rejects is a per-trait validation failure shown against that field. A required trait is met
 * by an entry being **present**, even with empty data (issue #826), and this page sends one for every trait -- an
 * untouched one as `{}` -- so from here the create gate is met whenever the save passes validation; what a trait
 * needs of its content is its schema's required fields, which fail as validation. A create save that does omit a
 * required trait (an API caller can) returns `saved=false` naming the unmet traits, and each named section says so.
 */
val WorkflowForm = FC<WorkflowFormProps> { props ->
    val wf = props.view
    val gedraId = props.gedraId
    val isEdit = gedraId != null
    // A link in the workflow's copy to a document opens it in the app (issue #1136).
    val copyLinks = useShellFacts().copyLinkResolver()

    // Seed every task's fields from its current entries (empty for a creation view). Trait ids are unique across
    // a workflow's tasks, so one map serves them all.
    val presentation = prefillPresentationOf(wf)
    val seeded: Map<String, Map<String, Any?>> = presentation.working

    // A creation form is always editable; a survey edit starts read-only (the "View Info" view) unless the URL
    // asked for edit mode (issue #694, the forms-list chip's direct-to-edit link).
    // A normal workflow's tasks are worked on only once the form is engaged and while the workflow is still
    // calculated (issue #791): before, the page offers Engage; after it closes (frozen), what it recorded stands.
    // The one task on screen when the survey shows a task at a time (issue #700), else null -- what Edit is offered on.
    val railMode = isEdit && wf.tasks.size > 1 && props.onSelectTask != null
    val shownTask = if (railMode) wf.tasks.firstOrNull { it.id == props.activeTask } ?: wf.tasks.firstOrNull() else null
    var editing by useState(if (isEdit) props.initialEditing == true && wf.offersEdit(shownTask) else true)
    // The user a create is being made for (issue #727), when an admin picked one; null is the self-create.
    // Meaningful only on a creation form; a survey edit never reads it.
    var pickedUser by useState<AdminUser?>(null)
    // Each of the four states a save settles is also held by its setter, which a save's continuation updates with a
    // transform: it runs after two round trips (the save, then the view's re-read, issue #827), and the values its
    // closure captured at the click are stale by then if the user typed on meanwhile.
    val valuesState = useState(seeded)
    var valuesByTrait by valuesState
    val updateValues = valuesState.component2()
    // Which supplied `filled` defaults are still suggestions (issue #710), by trait: seeded from the view's
    // filled defaults, a field leaving the set the first time it is touched (below) and a "reset" putting it
    // back. `offer` defaults are not seeded here -- they are not shown until applied, so they are never
    // "suggested". Empty for a form that carries no prefill, which is every form but the demo today.
    val suggestedState = useState(wf.tasks.flatMap { it.traits }.associate { t ->
        t.traitId to suggestedFilledFields(presentation, t.traitId)
    }.filterValues { it.isNotEmpty() })
    var suggestedByTrait by suggestedState
    val updateSuggested = suggestedState.component2()
    // Traits whose defaults are settled: once a task saves, its fields are stored as the user's own, so they
    // drop every default affordance (chip, "Use it", "reset") and read as ordinary fields (issue #710). The
    // `presentation` re-derives from the view the page re-reads after a save (issue #827), which returns those
    // entries as `source=user`, carrying nothing to present; this covers a save whose re-read failed.
    val resolvedState = useState(emptySet<String>())
    val resolvedTraits by resolvedState
    val updateResolved = resolvedState.component2()
    // The last-stored values, refreshed on each successful save -- what "unsaved" is measured against, so after a
    // save the saved task reads as clean rather than as differing from the first-render `seeded` snapshot.
    val storedState = useState(seeded)
    val stored by storedState
    val updateStored = storedState.component2()
    // An approval in flight (issue #832), by task, and a failure that never reached a verdict -- the network, a 5xx --
    // shown by that task's button. A refusal instead goes to the page, which reloads (see `onApproveSettled`).
    var approvingTask by useState<String?>(null)
    var approveFailure by useState<ApproveRefusal?>(null)
    var failuresByTrait by useState<Map<String, List<SchFailure>>>(emptyMap())
    // Commit-time validation (issue #718): per trait, the fields the user has committed (blurred, or picked), and
    // the traits checked as a whole -- by a Save, or by leaving the task. `failuresByTrait` always holds a trait's
    // full check; these two decide how much of it the panel shows, so a first blur does not flag every untouched
    // required field beneath it.
    var committedByTrait by useState<Map<String, Set<String>>>(emptyMap())
    var wholeChecked by useState<Set<String>>(emptySet())
    // Traits with a field committed since the last render, checked once the commit's new value is in `valuesByTrait`.
    var pendingChecks by useState<Set<String>>(emptySet())
    var unmetTraits by useState<Set<String>>(emptySet())
    var savingTask by useState<String?>(null)
    var runError by useState<DisplayError?>(null)
    // A save that landed but whose re-read of the view failed (issue #827): said beside the save rather than as a failed
    // save, since the data is stored -- only what the page shows of the form's state may be behind.
    var refreshError by useState<DisplayError?>(null)
    // Whether a survey edit has saved since the page opened -- the "✓ Saved." beside the header's actions.
    var justSaved by useState(false)

    // Design View (issue #972): on when this session asked for it and the backend answered with its block, which it
    // does only for a client administrator. What is selected and the two display switches are the page's own state.
    var designSelected by useState<DesignTarget?>(null)
    var designShowAllIds by useState(false)
    var designShowHidden by useState(true)
    val designSession = wf.design?.takeIf { designViewRequested() }?.let {
        DesignSession(
            it, designSelected, { t -> designSelected = t }, designShowAllIds, designShowHidden,
            // The form's client when it is another client's (issue #714's surface client): Design View reads and
            // saves there, as the view and the save do.
            workflowId = wf.workflowId, client = props.client, afterEdit = { props.onDesignEdited?.invoke() },
            isEdit = isEdit,
        )
    }
    // The inspector is fixed to the window's right edge; the page makes room for it rather than being covered.
    val designOpen = designSession != null
    useEffect(designOpen) {
        if (!designOpen) return@useEffect
        // An effect is a coroutine React cancels when it re-runs or the page unmounts (see DebugChoicePage): the
        // class is held for as long as this effect is, and removed in `finally`.
        document.body?.classList?.add(designOpenClass)
        try {
            awaitCancellation()
        } finally {
            document.body?.classList?.remove(designOpenClass)
        }
    }

    // Whether any task holds unsaved edits (issue #700): reported to the page when it changes, so the page can
    // arm the leave guard while there is something to lose and disarm it once saved or reverted.
    val anyUnsaved = isEdit && wf.tasks.any { taskUnsaved(it, valuesByTrait, stored) }
    useEffect(anyUnsaved) { props.onDirtyChange?.invoke(anyUnsaved) }

    fun valuesOf(traitId: String): Map<String, Any?> = valuesByTrait[traitId] ?: emptyMap()

    // The per-field supplied-default descriptors for a trait's form (issue #710): each defaulted field's mode, an
    // `offer`'s value for its "Use it" link, whether a `filled` default is still a suggestion, and -- once the
    // user has touched it -- a reset that restores the value and the mark. Applying an offer needs no callback:
    // it runs through the field's ordinary edit path in SchemaForm, so only the reset is wired here.
    fun prefillFor(traitId: String): Map<String, FieldPrefill> {
        if (traitId in resolvedTraits) return emptyMap()
        val modes = presentation.modes[traitId] ?: return emptyMap()
        val suggested = suggestedByTrait[traitId] ?: emptySet()
        val filledValues = presentation.working[traitId] ?: emptyMap()
        val offered = presentation.offered[traitId] ?: emptyMap()
        return modes.mapValues { (field, mode) ->
            if (mode == SLDM.offer) {
                FieldPrefill(mode = SLDM.offer, offeredValue = offered[field])
            } else {
                val isSuggested = field in suggested
                FieldPrefill(
                    mode = SLDM.filled,
                    suggested = isSuggested,
                    // Only a touched field offers a reset -- an untouched one already shows its suggestion.
                    onReset = if (isSuggested) null else ({
                        valuesByTrait = valuesByTrait +
                            (traitId to (valuesOf(traitId) + (field to filledValues[field])))
                        suggestedByTrait = suggestedByTrait +
                            (traitId to ((suggestedByTrait[traitId] ?: emptySet()) + field))
                    }),
                )
            }
        }
    }

    // The line shown when a task still holds supplied defaults (issue #710): the wording is overridable per
    // client through a task trait's layout `strings` (LAYSTR.prefillSummary), else the frontend's own copy;
    // both template over `${'$'}{count}`. Fail-safe: a broken override renders as written rather than blanking.
    fun prefillSummaryText(task: WfTaskView, count: Int): String {
        val override = task.traits.firstNotNullOfOrNull { it.fieldLayout?.strings?.get(LAYSTR.prefillSummary) }
        if (override != null) {
            return override.analyzeTemplate(evaluateWith = mapOf("count" to count)).value ?: override
        }
        val lead = if (count == 1) "1 field was" else "$count fields were"
        return "$lead filled in from your account — review and save to keep them."
    }

    // The save a task offers for this page's mode -- its edit save against an existing form, else its create save --
    // or none (issue #817): falling back to another kind would post a create against an existing form, and a task
    // with no save of this kind (a normal workflow's approval step, say) has nothing to post.
    fun saveFor(task: WfTaskView): WfSaveView? = saveOfKind(task, isEdit)

    fun onSave(task: WfTaskView) {
        // Client-side schema check per trait first; a failure keeps the save from leaving. A Save checks the
        // task's traits as a whole, so every failure shows from here on (issue #718), and a clean check clears
        // what an earlier one left.
        // A trait locked for this caller (issue #857) is not theirs to send: it stays as stored, and a save that
        // named it would be refused whole.
        val checks = task.traits.filterNot { it.traitId in wf.lockedTraits }
            .associate { it.traitId to checkFormInput(it.type, valuesOf(it.traitId), wf.fieldLayouts) }
        failuresByTrait = failuresByTrait + checks.mapValues { it.value.failures }
        wholeChecked = wholeChecked + checks.keys
        if (checks.values.any { it.failures.isNotEmpty() }) return

        // An edit sends only the fields its form shows (issue #1071): where the layout lists them, the save writes those
        // and refuses any other, so the hidden answers the page was seeded with stay home.
        val entries = workflowSaveEntries(
            checks.mapValues { (traitId, check) ->
                val layout = task.traits.firstOrNull { it.traitId == traitId }?.let { wf.fieldLayouts[it.typeName] }
                shownPayload(check.payload ?: emptyMap(), layout.takeIf { isEdit })
            },
        )
        // A create save may be for another user (issue #727) when an admin picked one; an edit ignores it.
        val forUserRef = if (isEdit) null else pickedUser?.primaryId
        val save = saveFor(task) ?: return
        val body = workflowSaveBody(wf.workflowId, task.id, save.id, entries, gedraId, forUserRef)
        // What this save sends, by trait, as the working values held it at the click -- what "stored" becomes for them.
        val savedTraitIds = task.traits.map { it.traitId }.toSet()
        val sent = valuesByTrait.filterKeys { it in savedTraitIds }
        savingTask = task.id
        runError = null
        refreshError = null
        // The address this save was launched under (#758 review): a create returns to the listing only while the
        // user is still on this page, and carries *this* listing context home -- not whatever page a slow
        // response finds them on.
        val launched = routeParams()
        wfFormScope.launch {
            // Set when the create's return is under way: the page is about to unmount, so Save stays busy rather
            // than coming back live for the tick before the router hears of the move -- a second click there would
            // make a second form. The sibling create pages skip the same reset for the same reason.
            var leaving = false
            try {
                val outcome = WorkflowApi.save(body, props.client)
                if (!outcome.saved) {
                    // The create gate: the required traits still empty.
                    unmetTraits = outcome.unmetTraits.toSet()
                } else if (!isEdit) {
                    // A create goes straight back to the listing it was launched from, the new row flashed (issue
                    // #758) -- the same return the trait-picker create makes (#663, #669), which this surface
                    // never got: it stopped on an in-place "Form created" page instead. A form whose survey still
                    // needs information says so on its row's status chip, which links into the survey editor.
                    formsCreateReturn(launched, routeParams(), outcome.item[GDF.gedraId] as? String)?.let {
                        leaving = true
                        navigate(it)
                    }
                } else {
                    justSaved = true
                    // A survey edit stays on the form and in edit mode -- a multi-task survey is saved one task
                    // at a time, so exiting or re-seeding the whole form here would discard the other tasks'
                    // in-progress edits. The save answers only that the form's state may have moved (issue #827),
                    // so the page re-reads the whole view in place, and the stored snapshot is re-taken from it --
                    // the same presented shape the seed used (filled prefill defaults seeded, offer ones held aside;
                    // issues #679/#710) -- then only *this* task's (possibly server-canonicalized) values are pushed
                    // into the fields; other tasks keep what the user has typed. The rail's marks, locks and
                    // approvals follow from the fresh view the page now holds. "Done" returns to read-only.
                    // Every state below is updated by transform, never from this closure's click-time values: the
                    // user may have typed on during the two round trips, and that must survive. A field of the saved
                    // task takes its stored value only while it still holds what was sent.
                    val refresh = props.refreshView
                    val refreshed = refresh?.let { apiResult { it(task.id).await() } }
                    val fresh = refreshed?.valueOrNull()
                    if (fresh != null) {
                        val storedNow = seedValuesOf(fresh)
                        updateStored { storedNow }
                        updateValues { current ->
                            current + storedNow.filterKeys { it in savedTraitIds && current[it] == sent[it] }
                        }
                    } else {
                        // The save landed but the view was not read back: what was sent is what is stored, so the
                        // task reads as saved. When a re-read was asked and failed, the note beside the save says
                        // the rest of the page may be behind; with none wired there is nothing to report.
                        if (refreshed != null) {
                            refreshError = refreshed.failureOrNull()?.let { userFacingError(it) }
                                ?: DisplayError.expected("The form no longer resolves.")
                        }
                        updateStored { it + sent }
                    }
                    // The saved task's fields are now the user's own stored data, not pending defaults, so
                    // drop their default affordances and summary (issue #710): out of the suggested set, and
                    // into the resolved set so `prefillFor` and the count stop treating them as defaults.
                    updateSuggested { current -> current.filterKeys { it !in savedTraitIds } }
                    updateResolved { it + savedTraitIds }
                }
            } catch (e: Throwable) {
                runError = userFacingError(e)
            } finally {
                if (!leaving) savingTask = null
            }
        }
    }

    // A field committed (issue #718): remember it, and have its whole trait re-checked from the working values -- the
    // panel shows the committed fields' failures now and the rest once the trait is checked as a whole.
    // The check waits for the next render rather than running here: a choice, checkbox, date, or file emits its new
    // value and commits in the same handler, so this closure still holds the value from *before* the pick. Checked
    // here, a first pick of a required choice was flagged as missing, and each later pick judged by the one before.
    fun onFieldCommit(trait: WfTraitView, path: String) {
        committedByTrait = committedByTrait + (trait.traitId to (committedByTrait[trait.traitId].orEmpty() + path))
        pendingChecks = pendingChecks + trait.traitId
    }
    useEffect(pendingChecks) {
        if (pendingChecks.isEmpty()) return@useEffect
        val traits = wf.tasks.flatMap { it.traits }.filter { it.traitId in pendingChecks }
        failuresByTrait = failuresByTrait + traits.associate { it.traitId to checkFormInput(it.type, valuesOf(it.traitId), wf.fieldLayouts).failures }
        pendingChecks = emptySet()
    }

    // Leaving a task in the rail checks it as a whole (issue #718): the user is done with it for now, so what
    // it still needs shows when they come back, and the mark reflects the check straight away.
    fun checkWholeTask(task: WfTaskView) {
        failuresByTrait = failuresByTrait + task.traits.associate { it.traitId to checkFormInput(it.type, valuesOf(it.traitId), wf.fieldLayouts).failures }
        wholeChecked = wholeChecked + task.traits.map { it.traitId }
    }

    // The status a rail entry draws (issue #718): while the task holds unsaved edits, the client's projection
    // from the working values -- what the server will say once they are saved, by the same rule -- so clearing
    // a required field drops the check at once; otherwise the server's verdict from the view (re-read after a save),
    // which stays the authority for anything the kernel check cannot see.
    fun statusFor(task: WfTaskView, unsaved: Boolean): WfTaskStatus? =
        if (unsaved) localTaskStatus(task, valuesByTrait, wf.fieldLayouts) else task.status

    // An approval task drawn in its own way (issue #832): once approved, who approved it and when; before, the prompt
    // and -- for a reviewer -- the button, which asks first, since an approval cannot be taken back. Anyone else is
    // told the step waits on a reviewer rather than shown a button the endpoint could only refuse. Not tied to Edit:
    // approving is its own act, not an edit of the form. The endpoint decides who may and when; this is presentation.
    // The last approval's refusal or failure for [task] only -- another approval task's never shows here -- and not
    // while a new attempt is in flight. Drawn under the approval, or under a text display standing in for it, so a
    // refusal the reload turned into "already approved" still says the click did not take.
    fun ChildrenBuilder.approvalNotice(task: WfTaskView) {
        val shown = (approveFailure ?: props.approveRefusal)?.takeIf { it.taskId == task.id && approvingTask == null }
        shown?.let { errorText("Couldn't approve the form.", it.error) }
    }

    fun ChildrenBuilder.approvalBlock(task: WfTaskView, approval: WfApprovalView) {
        // Approving reloads the view, which starts every task's working copy afresh -- so while any task holds edits
        // not yet saved, approving waits, rather than silently dropping them (and approving what they would change).
        val anyUnsaved = wf.tasks.any { taskUnsaved(it, valuesByTrait, stored) }
        val busy = approvingTask != null
        div {
            className = ClassName("wf-approval")
            when {
                approval.approved -> p { +approvedLine(approval) }
                else -> {
                    if (approval.prompt.isNotBlank()) Markdown { source = approval.prompt; linkResolver = copyLinks }
                    val gid = gedraId
                    if (!task.isReviewer || gid == null) {
                        p {
                            className = ClassName("subtitle")
                            +"Waiting for a reviewer to approve this."
                        }
                    } else {
                        val blocked = task.isDisabled || busy || anyUnsaved
                        div {
                            className = ClassName("row")
                            Popconfirm {
                                title = "Approve this form?"
                                description = "An approval can't be taken back."
                                okText = approval.button.ifBlank { "Approve" }
                                cancelText = "Cancel"
                                disabled = blocked
                                onConfirm = {
                                    approvingTask = task.id
                                    approveFailure = null
                                    wfFormScope.launch {
                                        try {
                                            WorkflowApi.approve(gid, wf.workflowId, task.id, props.client)
                                            props.onApproveSettled?.invoke(task.id, null)
                                        } catch (e: Throwable) {
                                            val shown = userFacingError(e)
                                            // A verdict (a 4xx) means the state may have moved -- approved by someone
                                            // else, say -- so the page reloads to show it, keeping the refusal.
                                            val status = (e as? ApiError)?.status
                                            if (status != null && status in 400..499 && props.onApproveSettled != null) {
                                                props.onApproveSettled?.invoke(task.id, shown)
                                            } else {
                                                approveFailure = ApproveRefusal(task.id, shown)
                                            }
                                        } finally {
                                            approvingTask = null
                                        }
                                    }
                                }
                                Button {
                                    type = "primary"
                                    loading = approvingTask == task.id
                                    disabled = blocked
                                    +approval.button.ifBlank { "Approve" }
                                }
                            }
                        }
                        if (anyUnsaved) {
                            p {
                                className = ClassName("subtitle")
                                +"Save or undo your changes before approving."
                            }
                        }
                    }
                }
            }
            approvalNotice(task)
        }
    }

    // One task's body -- the "TaskPanel" (issue #700): its label (when something else does not already name the
    // task, issue #719), each trait's form, and its Save while editing. In rail mode one of these shows at a
    // time; in single-panel mode each task's shows in turn.
    fun ChildrenBuilder.taskPanel(task: WfTaskView, showLabel: Boolean = true) {
        div {
            // A disabled display (issue #788) greys the whole step; nothing in it acts (see `isEditable`).
            className = ClassName(if (task.isDisabled) "wf-task wf-task-disabled" else "wf-task")
            if (showLabel && wf.showTaskList && task.label.isNotBlank()) {
                designFramed(designSession, DesignTarget.Task(task), task.id) { Markdown { source = task.label; inlineUi = true; linkResolver = copyLinks } }
            }
            // A text display (issue #788) stands in for the task's whole rendering -- fields, saves, an approval's
            // button -- with its placeholders filled from the task's own data.
            displayTextOf(task)?.let { text ->
                div {
                    className = ClassName("wf-display-text")
                    Markdown { source = text; linkResolver = copyLinks }
                }
                approvalNotice(task)
                return@div
            }
            // A step with fields this caller may not change (issue #856): say so, rather than leave a read-only
            // panel among editable ones looking broken.
            // A form kept to its owner says so once, above (issue #1433), rather than on every step.
            if (!task.canSave && task.traits.isNotEmpty() && !wf.keptToOwner) {
                p {
                    className = ClassName("subtitle")
                    +"Someone else completes this step; you can see it here."
                }
            }
            // An approval task's own rendering (issue #832): its prompt and, for a reviewer, the button.
            task.approval?.let { approval -> approvalBlock(task, approval) }
            // Any other step that collects nothing has no fields to draw; the panel says so rather than showing an
            // empty card.
            if (task.traits.isEmpty() && task.approval == null) {
                p {
                    className = ClassName("subtitle")
                    +"There is nothing to fill in for this step here."
                }
            }
            task.traits.forEach { trait ->
                designFramed(designSession, DesignTarget.Trait(trait), trait.traitId) {
                div {
                    className = ClassName("wf-trait")
                    // Its name as the section's heading, and its introduction under it as a muted caption (issue #1534):
                    // the layout's `label` -- inline Markdown, one line -- else the trait's name by the rule
                    // ([traitSectionLabel]), then its `description`. One block, so the caption sits directly under the
                    // heading (issue #1327's look).
                    val label = traitSectionLabel(trait)
                    val intro = trait.fieldLayout?.description?.takeIf { it.isNotBlank() }
                    if (label == null && intro == null) {
                        h2 { +traitName(trait) }
                    } else {
                        val heading = label ?: traitName(trait).escapeMarkdown()
                        Markdown {
                            source = "## $heading" + (intro?.let { "\n\n$it" } ?: "")
                            inlineUi = true
                            linkResolver = copyLinks
                        }
                    }
                    // Locked for this caller (issue #857): shown as stored, and said why -- but only on a task they
                    // could otherwise save. On the locking workflow's own task the lock exempts exactly that task's
                    // savers, so the step is already read-only for them and said to be someone else's above.
                    wf.lockedTraits[trait.traitId]?.takeIf { task.canSave }?.let { held ->
                        p {
                            className = ClassName("subtitle")
                            +"Locked by ${held.joinToString(" and ") { it.label }}; it can't be changed here."
                        }
                    }
                    if (trait.traitId in unmetTraits) {
                        p {
                            className = ClassName("error-text")
                            +"This is required — please fill it in."
                        }
                    }
                    // Design View marks this trait's fields (issue #972); outside it the provider carries null.
                    DesignViewContext.Provider {
                        value = designSession?.forTrait(trait)
                        SchemaForm {
                            type = trait.type
                            this.values = valuesOf(trait.traitId)
                            // A step this caller may not save (issue #856) or that has no save of this page's kind (issue
                            // #817), or a trait locked for them (issue #857), stays read-only while the rest is edited.
                            editable = editing && wf.isEditable(task, isEdit) && trait.traitId !in wf.lockedTraits
                            friendly = true
                            // In the read-only "View Info" view, show a trait's derived data values (issue #712) --
                            // an expense report's total, computed by the backend -- rather than hiding them; the flag is
                            // inert while editing, where a derived field has no control to draw. This form's root is
                            // one trait's data type, so every derived-with-value field is content, not envelope.
                            showDerivedValues = true
                            derivedRootIsTraitData = true
                            this.cfacts = wf.cfacts
                            this.fieldLayouts = wf.fieldLayouts
                            this.failures = shownFailures(
                                failuresByTrait[trait.traitId].orEmpty(), committedByTrait[trait.traitId].orEmpty(),
                                trait.traitId in wholeChecked,
                            )
                            this.prefill = prefillFor(trait.traitId)
                            onChange = { valuesByTrait = valuesByTrait + (trait.traitId to it) }
                            onFieldEdit = { field ->
                                if (trait.traitId in unmetTraits) unmetTraits = unmetTraits - trait.traitId
                                // First touch of a suggested default makes it the user's (issue #710); a reset can
                                // bring it back. A nested field's path never matches a root default's name, so only
                                // the top-level default it belongs to is un-suggested.
                                val suggested = suggestedByTrait[trait.traitId]
                                if (suggested != null && field in suggested) {
                                    suggestedByTrait = suggestedByTrait + (trait.traitId to (suggested - field))
                                }
                            }
                            onFieldCommit = { path -> onFieldCommit(trait, path) }
                        }
                    }
                }
                }
            }
            // The save is per task (each task's own entries), shown only while editing. Disabled while there is
            // nothing to save (issue #717) -- the working values match the stored snapshot, the same comparison
            // that drives the rail's unsaved badge, so a prefilled task the user never touched offers no Save
            // and a saved task's Save disables itself once the snapshot refreshes -- and while ANY task's save
            // is in flight: `loading` blocks only the button being saved, and single-panel mode shows
            // every task's. Disabled means "nothing to do", never "you did it wrong": a task with known
            // validation failures keeps its Save, so clicking it shows the errors rather than a dead button.
            // A task that offers no save -- a normal workflow's approval step (issues #787, #791) -- draws none:
            // there is nothing here to save, and asking for its save would find none.
            val save = saveFor(task)
            if (editing && wf.isEditable(task, isEdit) && save != null) {
                // When defaults are the only thing left to do, say so above the Save (issue #710): the count is
                // the task's still-pending defaults, and it disappears as they are accepted, edited, or saved.
                val pendingDefaults =
                    pendingDefaultCount(task, presentation, suggestedByTrait, valuesByTrait, resolvedTraits)
                if (pendingDefaults > 0) {
                    p {
                        className = ClassName("form-prefill-summary")
                        +prefillSummaryText(task, pendingDefaults)
                    }
                }
                div {
                    className = ClassName("row")
                    // Design View frames the button (issue #1070); its badge selects it even while the button is disabled.
                    designFramed(designSession, DesignTarget.Save(task, save), save.id) {
                        Button {
                            type = "primary"
                            loading = savingTask == task.id
                            disabled = !taskUnsaved(task, valuesByTrait, stored) || savingTask != null
                            onClick = { onSave(task) }
                            +save.label
                        }
                    }
                }
            }
        }
    }

    // One rail entry (issue #700): the task's label, its status mark, and an unsaved badge when its working values
    // differ from the stored ones. A real button, so it is keyboard reachable; `aria-current` names the open
    // task, and the title / aria-label carry what the mark means -- a hover tooltip alone is not enough for
    // touch or a screen reader.
    fun ChildrenBuilder.railItem(task: WfTaskView, active: Boolean) {
        val unsaved = taskUnsaved(task, valuesByTrait, stored)
        val status = statusFor(task, unsaved)
        val mark = railMark(status)
        // Name a missing trait by its name (issue #1520), so the tooltip and the panel's heading agree.
        val explanation = railExplanation(status, unsaved) { id ->
            task.traits.firstOrNull { it.traitId == id }?.let(::traitName) ?: humanizeFieldName(id)
        }
        button {
            className = ClassName(
                listOfNotNull("wf-rail-item", "active".takeIf { active }, "disabled".takeIf { task.isDisabled }).joinToString(" "),
            )
            title = explanation
            asDynamic()["aria-current"] = if (active) "true" else "false"
            asDynamic()["aria-label"] = "${task.label}: $explanation"
            // A disabled display (issue #788): its rail entry is not a way in. `aria-disabled` rather than `disabled`,
            // so it stays focusable and keeps its tooltip -- a disabled button gets no pointer events in some
            // browsers -- and the click below does nothing. The open task stays shown, greyed.
            if (task.isDisabled && !active) asDynamic()["aria-disabled"] = "true"
            onClick = {
                if (!active && !task.isDisabled) {
                    // The task being left gets its whole check before the switch (issue #718).
                    if (editing) wf.tasks.firstOrNull { it.id == props.activeTask }?.let { checkWholeTask(it) }
                    props.onSelectTask?.invoke(task.id)
                }
            }
            span {
                className = ClassName("wf-mark wf-mark-${mark.name}")
                +(if (mark == RailMark.incomplete) "○" else "✓")
            }
            span {
                className = ClassName("wf-rail-label")
                +task.label
            }
            if (unsaved) {
                span {
                    className = ClassName("wf-unsaved")
                    +"●"
                }
            }
        }
    }

    div {
        className = ClassName("card wide")
        // The page's title (issue #719): the workflow's own label when its definition gives one, else the
        // generic title. Inline markdown, the phrase renderer a task label's copy already goes through.
        val title = wf.label.ifBlank { if (isEdit) "Edit form" else "New form" }

        // One header line, shared with the raw editor (issues #719, #726): the back link, the title beside
        // it, and the actions right-aligned. The back link (issue #671 for the create fill-out, #694 for the
        // survey's views, including an arrival straight into edit mode from the status chip) is the shared
        // forms link, so it returns to the same filtered, sorted listing. The survey edit's actions are Edit
        // over the read-only "View Info" (with the raw editor beside it) and Done while editing, with the
        // saved note beside either.
        formsEditorHeader(title = {
            designFramed(designSession, DesignTarget.Workflow, wf.workflowId) { MarkdownInline { source = title; linkResolver = copyLinks } }
        }) {
            if (isEdit) {
                if (editing) {
                    Button {
                        // Done is the one way home from an edit (issue #726): back to the listing, with this
                        // form flashed, carrying the listing's search and sort -- the same return the raw
                        // editor's Done makes. It is a navigation, so the leave guard the page armed on
                        // unsaved edits asks through the router (issue #716); nothing to ask here, and asking
                        // here too would prompt twice. A clean Done just leaves.
                        onClick = { navigate(formsListingReturn(routeParams(), gedraId)) }
                        +"Done"
                    }
                } else {
                    // Only over a step the caller can edit (issue #857 UI pass): on someone else's step, Edit would
                    // swap for Done and leave the step exactly as it was.
                    if (wf.offersEdit(shownTask)) {
                        Button {
                            type = "primary"
                            onClick = { editing = true }
                            +"Edit"
                        }
                    }
                    // A normal workflow the form is not in yet (issue #791): Engage first, then its tasks open.
                    props.onEngage?.takeIf { wf.canEngage }?.let { engage ->
                        Button {
                            type = "primary"
                            onClick = { engage() }
                            +"Engage"
                        }
                    }
                    // The raw read-only view (issue #726): every trait, including the ones the survey does
                    // not show, without entering the editor -- the look-before-editing counterpart of the
                    // raw editor beside it.
                    props.onRawView?.let { rawView ->
                        Button {
                            type = "link"
                            onClick = { rawView() }
                            +"View raw"
                        }
                    }
                    // The raw editor, for the traits the survey does not show (issue #694): offered only
                    // when the caller's surface carries the patch endpoint (the page decides; null hides it).
                    props.onRawEdit?.let { rawEdit ->
                        Button {
                            type = "link"
                            onClick = { rawEdit() }
                            +"Raw edit"
                        }
                    }
                }
                if (justSaved) {
                    p {
                        className = ClassName("form-ok")
                        +"✓ Saved."
                    }
                }
            }
        }

        // Help the workflow's copy gives it (issue #1221), under the title: what this form is for, or where to read more.
        if (wf.help.isNotBlank()) {
            div {
                className = ClassName("wf-help")
                Markdown { source = wf.help; linkResolver = copyLinks }
            }
        }
        // Why a normal workflow's tasks cannot be worked on here (issue #791), when they cannot.
        workflowNote(wf)?.let { note ->
            p {
                className = ClassName("subtitle")
                +note
            }
        }
        // The reasons, when it is eligibility that stands in the way (issue #791) -- the same explanations the forms
        // list's dialog shows, in place of an Engage that could only be refused.
        if (wf.engaged == false && wf.eligible == false && wf.ineligibleReasons.isNotEmpty()) {
            ul {
                className = ClassName("wf-reasons")
                wf.ineligibleReasons.forEachIndexed { i, r ->
                    li {
                        key = i.toString().unsafeCast<Key>()
                        MarkdownInline { source = r; linkResolver = copyLinks }
                    }
                }
            }
        }

        // Create for another user (issue #727), admin-only and creation-only: the picker chooses whose form
        // it is; the workflow's fields are still this client's. Blank creates it for the caller.
        if (!isEdit && props.allowCreateForUser == true) {
            FormUserPicker { onPick = { pickedUser = it } }
        }

        // A multi-task survey shows ONE task at a time, picked from the rail (issue #700) -- so there is one
        // Save in view, and the other tasks' working values stay in state across the switch. A single-task
        // survey and the creation form keep the single panel with their one task.
        val active = props.activeTask
        if (railMode) {
            div {
                className = ClassName("wf-layout")
                div {
                    className = ClassName("wf-rail")
                    // In Design View each entry is framed as its task (issue #1070): a click both opens the task and
                    // selects it.
                    wf.tasks.forEach { task -> designFramed(designSession, DesignTarget.Task(task), task.id) { railItem(task, active = task.id == active) } }
                }
                div {
                    className = ClassName("wf-panel")
                    // The rail entry the user just clicked is the task's label; the panel does not repeat it
                    // (issue #719) and opens on the trait heading.
                    taskPanel(shownTask ?: wf.tasks.first(), showLabel = false)
                }
            }
        } else {
            wf.tasks.forEach { taskPanel(it) }
        }

        if (unmetTraits.isNotEmpty()) {
            p {
                className = ClassName("form-stale")
                +"Some required sections are empty — they are marked above."
            }
        }
        runError?.let { errorText(if (isEdit) "Couldn't save the form." else "Couldn't create the form.", it) }
        refreshError?.let { errorText("Saved, but couldn't reload the form, so what it shows may be out of date.", it) }
        designSession?.let { session ->
            DesignInspector {
                this.session = session
                this.view = wf
                onShowAllIds = { designShowAllIds = it }
                onShowHidden = { designShowHidden = it }
                onClear = { designSelected = null }
            }
        }
    }
}

/** The body class that makes room for Design View's inspector (issue #972); see `body.dv-open` in app.css. */
private const val designOpenClass = "dv-open"

/** [content] framed for Design View as [target] when there is a [session], else drawn as it is (issue #972). */
private fun ChildrenBuilder.designFramed(
    session: DesignSession?,
    target: DesignTarget,
    label: String,
    content: ChildrenBuilder.() -> Unit,
) {
    if (session == null) content() else designTargetFrame(session, target, label, content = content)
}

/**
 * Why a normal workflow's tasks are read-only on this page (issue #791), or null when they are not (or the view is
 * a creation or survey) -- or, for any view, that the form is kept to its owner from this caller (issue #1433). Pure,
 * and covered under `jsNodeTest`.
 */
fun workflowNote(wf: WorkflowView): String? = when {
    // Kept to its owner (issue #1433): said once for the whole form, whatever kind of workflow draws it.
    wf.keptToOwner -> TraitLockCopy.keptToOwner
    !wf.isNormal || wf.canWork -> null
    wf.canEngage -> "This form is not in this workflow yet. Engage it to start."
    wf.engaged == false && wf.eligible == false -> "This form cannot be put into this workflow yet:"
    wf.phase == WfPhase.lifetimeOnly.name -> "This workflow has closed. What it recorded stands, and it can no longer be changed."
    // Engaged and open, but no step here is this caller's to save (issue #856): each step says so for itself.
    wf.engaged == true -> null
    else -> "This form is not in this workflow, and it is not taking new forms."
}

/**
 * A trait's name (issue #1520), by the rule every page names a trait with: its layout `label`, its title, else its id
 * humanized. A section with a layout `label` that says something shows it as the heading instead, Markdown and all
 * ([traitSectionLabel]); this is its fallback.
 */
private fun traitName(trait: WfTraitView): String = traitNameOf(trait.traitId, trait.fieldLayout?.label, trait.type.title)

external interface LoadStateCardProps : Props {
    /** The page heading, shown while it loads or fails ("New form", "Edit form"). */
    var title: String

    /** The load failure to show; null renders the "Loading…" state instead. */
    var loadError: DisplayError?

    /** What the failure is a failure of ("Couldn't load the clients."); the form pages' wording when unset. */
    var errorLead: String?
}

/**
 * The load-state chrome shared by the workflow pages ([CreationPage], [SurveyEditPage]): a `card wide` carrying
 * the page's [LoadStateCardProps.title] and either "Loading…" or the load error. Keeps the two pages from each
 * hand-rolling the same scaffolding that differs only in the heading text.
 */
val LoadStateCard = FC<LoadStateCardProps> { props ->
    div {
        className = ClassName("card wide")
        h1 { +props.title }
        val err = props.loadError
        if (err == null) {
            p {
                className = ClassName("subtitle")
                +"Loading…"
            }
        } else {
            errorText(props.errorLead ?: "Couldn't load the form.", err)
        }
    }
}
